import { useState, useEffect, useCallback, useRef } from 'react';
import { todayInTimezone, isTodayInTimezone } from '@/utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  UserX, 
  Clock, 
  AlertCircle, 
  Coffee, 
  CheckCircle,
  RefreshCcw,
  LogIn,
  LogOut,
  Timer,
  Zap,
  Search,
  Pause,
  Globe,
  Smartphone,
  Monitor,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Table2,
} from 'lucide-react';
import { formatTimeFromISO } from '@/utils/timezoneUtils';
import { useLanguage } from '@/i18n/LanguageContext';

// Helper to check if last_active is more than 10 minutes ago
const isDeviceInactive = (lastActive) => {
  if (!lastActive) return true;
  const lastActiveDate = new Date(lastActive);
  const now = new Date();
  const diffMinutes = (now - lastActiveDate) / (1000 * 60);
  return diffMinutes > 10;
};

// Format last active time for tooltip
const formatLastActive = (lastActive) => {
  if (!lastActive) return 'Never';
  const date = new Date(lastActive);
  const now = new Date();
  const diffMinutes = Math.floor((now - date) / (1000 * 60));
  
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ${diffMinutes % 60}m ago`;
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Device type icon helper: 1=browser(Globe), 2=mobile(Smartphone), 3=desktop(Monitor)
// Shows red color if device is inactive (no heartbeat in 10 minutes)
const DeviceIcon = ({ deviceType, lastActive, isActive, className = "w-3 h-3", isRecentlyActive = null }) => {
  const inactive = isActive && (typeof isRecentlyActive === 'boolean' ? !isRecentlyActive : isDeviceInactive(lastActive));
  const activeColor = inactive ? 'text-red-500' : '';
  
  const getIcon = () => {
    switch (deviceType) {
      case 1:
        return <Globe className={`${className} ${activeColor || 'text-blue-500'}`} />;
      case 2:
        return <Smartphone className={`${className} ${activeColor || 'text-indigo-500'}`} />;
      case 3:
        return <Monitor className={`${className} ${activeColor || 'text-purple-500'}`} />;
      default:
        return null;
    }
  };
  
  const getDeviceName = () => {
    switch (deviceType) {
      case 1: return 'Browser';
      case 2: return 'Mobile';
      case 3: return 'Desktop';
      default: return 'Unknown';
    }
  };
  
  if (!deviceType) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-pointer inline-flex">
            {getIcon()}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs">
            <p className="font-semibold">{getDeviceName()}</p>
            <p className={inactive ? 'text-red-400' : 'text-indigo-400'}>
              {inactive ? '⚠ Inactive' : '● Active'}
            </p>
            <p className="text-gray-400">Last active: {formatLastActive(lastActive)}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

function AttendancePage({ socket }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [attendanceData, setAttendanceData] = useState([]);
  const [todayStats, setTodayStats] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedDate, setSelectedDate] = useState('');  // empty until backend confirms correct date
  const [companyTimezone, setCompanyTimezone] = useState(() => localStorage.getItem('company_timezone') || 'UTC');
  const [nextDayStartTime, setNextDayStartTime] = useState(() => localStorage.getItem('next_day_start_time') || '00:00');
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_attendance_view');
      return saved === 'table' || saved === 'cards' ? saved : 'cards';
    } catch {
      return 'cards';
    }
  });
  const timerRef = useRef(null);
  const autoRefreshRef = useRef(null);
  const initialDateSet = useRef(false);
  const isToday = isTodayInTimezone(selectedDate, companyTimezone);

  // Define fetch functions first with useCallback
  const fetchAttendance = useCallback(async () => {
    try {
      // On first load: don't send date — backend computes correct date in company timezone
      // On subsequent calls: send selectedDate (user-picked or backend-corrected date)
      const params = initialDateSet.current && selectedDate ? { date: selectedDate } : {};

      const response = await axios.get(`${API}/admin/attendance`, {
        params,
        withCredentials: true
      });
      
      let data = response.data.records || response.data;
      const timezone = response.data.timezone || 'UTC';
      const startTime = response.data.next_day_start_time || '00:00';
      const backendDate = response.data.date;  // backend-computed date in company timezone
      
      // Store company timezone and next day start time
      setCompanyTimezone(timezone);
      setNextDayStartTime(startTime);
      localStorage.setItem('company_timezone', timezone);
      localStorage.setItem('next_day_start_time', startTime);

      // On first load: use the backend-computed date as the source of truth
      if (!initialDateSet.current) {
        initialDateSet.current = true;
        if (backendDate) {
          setSelectedDate(backendDate);
        }
        // If backend returned data for the correct date, use it directly
        setAttendanceData(data);
        setLastUpdate(new Date());
        return;
      }

      setAttendanceData(data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);
  
  const fetchTodayStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/attendance/today-stats?date=${selectedDate}`, {
        withCredentials: true
      });
      setTodayStats(response.data.staff || []);
    } catch (error) {
      console.error('Failed to fetch today stats:', error);
    }
  }, [selectedDate]);
  
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);
  
  // Update current time every second for activity timers
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);
  
  // Initial data fetch
  useEffect(() => {
    fetchAttendance();
    fetchStats();
    fetchTodayStats();
  }, [selectedDate, fetchAttendance, fetchStats, fetchTodayStats]);
  
  // Auto-refresh every 5 seconds as fallback when WebSocket is not connected
  useEffect(() => {
    if (!isConnected) {
      autoRefreshRef.current = setInterval(() => {
        fetchAttendance();
        fetchStats();
        fetchTodayStats();
      }, 5000);
    }
    
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [isConnected, fetchAttendance, fetchStats, fetchTodayStats]);
  
  // Real-time WebSocket updates
  useEffect(() => {
    // Listen for window events (from parent component's socket)
    const handleWindowUpdate = () => {
      console.log('Window attendance update received');
      fetchAttendance();
      fetchStats();
    };
    
    window.addEventListener('attendance_update', handleWindowUpdate);
    
    // Also set up direct socket listeners if socket is available
    if (socket) {
      const handleConnect = () => {
        console.log('WebSocket connected for attendance');
        setIsConnected(true);
        socket.emit('subscribe_dashboard', {});
      };
      
      const handleDisconnect = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
      };
      
      const handleAttendanceUpdate = () => {
        console.log('Real-time attendance update received');
        fetchAttendance();
        fetchStats();
      };
      
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('attendance_update', handleAttendanceUpdate);
      
      // Check if already connected
      if (socket.connected) {
        setIsConnected(true);
      }
      
      return () => {
        window.removeEventListener('attendance_update', handleWindowUpdate);
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('attendance_update', handleAttendanceUpdate);
      };
    }
    
    return () => {
      window.removeEventListener('attendance_update', handleWindowUpdate);
    };
  }, [socket, fetchAttendance, fetchStats]);
  
  const handleMarkAttendance = async (staffId, status) => {
    try {
      await axios.post(
        `${API}/admin/attendance/mark`,
        {
          staff_id: staffId,
          status: status,
          check_in: status === 'present' ? null : (status !== 'absent' ? new Date().toISOString() : null),
          check_out: null
        },
        { withCredentials: true }
      );
      
      fetchAttendance();
      fetchStats();
    } catch (error) {
      console.error('Failed to mark attendance:', error);
    }
  };
  
  // Handle Force Break
  const handleForceBreak = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to put ${staffName} on a Manual Force Break?`)) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/staff/force-break`,
        { staff_id: staffId },
        { withCredentials: true }
      );
      
      fetchAttendance();
      fetchStats();
      if (isToday) {
        fetchTodayStats();
      }
    } catch (error) {
      console.error('Failed to force break:', error);
      alert(error.response?.data?.detail || 'Failed to start force break');
    }
  };
  
  // Handle End Force Break
  const handleEndForceBreak = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to end ${staffName}'s break?`)) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/staff/end-force-break`,
        { staff_id: staffId },
        { withCredentials: true }
      );
      
      fetchAttendance();
      fetchStats();
      if (isToday) {
        fetchTodayStats();
      }
    } catch (error) {
      console.error('Failed to end break:', error);
      alert(error.response?.data?.detail || 'Failed to end break');
    }
  };
  
  // Handle Force Logout
  const handleForceLogout = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to force logout ${staffName}? This will check them out immediately.`)) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/staff/force-logout`,
        { staff_id: staffId },
        { withCredentials: true }
      );
      
      fetchAttendance();
      fetchStats();
      if (isToday) {
        fetchTodayStats();
      }
    } catch (error) {
      console.error('Failed to force logout:', error);
      alert(error.response?.data?.detail || 'Failed to force logout');
    }
  };
  
  // Calculate detailed stats from attendance data
  const calculateDetailedStats = () => {
    let totalCheckIn = 0;  // Currently checked in (not checked out)
    let totalCheckOut = 0;
    let totalAbsent = 0;
    let totalLate = 0;
    let notMarked = 0;
    let onBreak = 0;
    let ready = 0;
    let forceBreak = 0;
    
    attendanceData.forEach(item => {
      if (item.attendance) {
        // Count currently checked in (has check_in but NO check_out)
        if (item.attendance.check_in && !item.attendance.check_out) {
          totalCheckIn++;
        }
        // Count checked out
        if (item.attendance.check_out) {
          totalCheckOut++;
        }
        if (item.attendance.status === 'absent') totalAbsent++;
        if (item.attendance.status === 'late') totalLate++;
        if (item.attendance.status === 'day_off') totalAbsent++; // Count day_off separately or with absent
        if (item.attendance.status === 'not_marked' || !item.attendance.status) notMarked++;
        
        // Check if on break (has an active break without end_time)
        const hasActiveBreak = item.attendance.breaks?.some(b => b.start_time && !b.end_time);
        if (hasActiveBreak) {
          onBreak++;
          // Check if force break
          if (item.attendance.breaks?.some(b => b.break_type === 'force' && !b.end_time)) {
            forceBreak++;
          }
        } else if (item.attendance.check_in && !item.attendance.check_out) {
          ready++;
        }
      } else {
        notMarked++;
      }
    });
    
    return {
      totalCheckIn,
      totalCheckOut,
      totalAbsent,
      totalLate,
      notMarked,
      onBreak,
      ready,
      forceBreak
    };
  };
  
  const detailedStats = calculateDetailedStats();
  
  // Calculate activity status and duration
  const getActivityStatus = (item) => {
    if (!item.attendance || !item.attendance.check_in) {
      return { status: 'not_checked_in', duration: null, label: '-' };
    }
    
    // Check for active break
    const activeBreak = item.attendance.breaks?.find(b => b.start_time && !b.end_time);
    if (activeBreak) {
      const breakStart = new Date(activeBreak.start_time);
      const duration = Math.max(0, Math.floor((currentTime - breakStart) / 1000));
      return {
        status: 'on_break',
        duration,
        label: `Break (${formatDuration(duration)})`,
        breakType: activeBreak.break_type
      };
    }
    
    // If checked in and not on break = Ready
    if (item.attendance.check_in && !item.attendance.check_out) {
      const checkInTime = new Date(item.attendance.check_in);
      const duration = Math.max(0, Math.floor((currentTime - checkInTime) / 1000));
      return {
        status: 'ready',
        duration,
        label: `Ready (${formatDuration(duration)})`
      };
    }
    
    // Checked out
    if (item.attendance.check_out) {
      // Check if it was a force checkout
      if (item.attendance.force_checkout) {
        return { status: 'force_checked_out', duration: null, label: 'Force Checked Out' };
      }
      return { status: 'checked_out', duration: null, label: 'Checked Out' };
    }
    
    return { status: 'unknown', duration: null, label: '-' };
  };
  
  // Format duration as HH:MM:SS
  const formatDuration = (seconds) => {
    const totalSecs = Math.round(seconds || 0); // round to avoid float precision like 23.9999...
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Format minutes to hours and minutes
  const formatMinutesToTime = (minutes) => {
    if (!minutes || minutes === 0) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m`;
  };
  
  // Get today stats for a staff member
  const getTodayStatsForStaff = (staffId) => {
    return todayStats.find(s => s.staff_id === staffId) || null;
  };
  
  // Calculate total break time for an attendance record (current session only)
  const getTotalBreakTime = (item) => {
    if (!item.attendance?.breaks?.length) return 0;
    
    let totalSeconds = 0;
    item.attendance.breaks.forEach(brk => {
      if (brk.start_time) {
        const start = new Date(brk.start_time);
        const end = brk.end_time ? new Date(brk.end_time) : currentTime;
        totalSeconds += Math.max(0, Math.floor((end - start) / 1000));
      }
    });
    return totalSeconds;
  };

  // Get ONLY the ongoing (active, no end_time) break duration in seconds
  const getOngoingBreakSeconds = (item) => {
    if (!item.attendance?.breaks) return 0;
    const activeBreak = item.attendance.breaks.find(b => b.start_time && !b.end_time);
    if (!activeBreak) return 0;
    return Math.max(0, Math.floor((currentTime - new Date(activeBreak.start_time)) / 1000));
  };

  // Calculate total work time in real-time (check_in to now, minus breaks, current session only)
  const getTotalWorkTime = (item) => {
    if (!item.attendance?.check_in) return 0;
    const checkIn = new Date(item.attendance.check_in);
    const endTime = item.attendance?.check_out ? new Date(item.attendance.check_out) : currentTime;
    const totalSeconds = Math.max(0, Math.floor((endTime - checkIn) / 1000));
    const breakSeconds = getTotalBreakTime(item);
    return Math.max(0, totalSeconds - breakSeconds);
  };
  
  // Sort attendance data - staff on break with longest break time first
  const sortedAttendanceData = [...attendanceData].sort((a, b) => {
    const aActivity = getActivityStatus(a);
    const bActivity = getActivityStatus(b);
    
    // On break staff first
    if (aActivity.status === 'on_break' && bActivity.status !== 'on_break') return -1;
    if (bActivity.status === 'on_break' && aActivity.status !== 'on_break') return 1;
    
    // Sort by break duration (longest first)
    if (aActivity.status === 'on_break' && bActivity.status === 'on_break') {
      return (bActivity.duration || 0) - (aActivity.duration || 0);
    }
    
    // Then ready staff
    if (aActivity.status === 'ready' && bActivity.status !== 'ready') return -1;
    if (bActivity.status === 'ready' && aActivity.status !== 'ready') return 1;
    
    return 0;
  });
  
  // Filter attendance data based on search term (case-insensitive, partial match)
  const filteredAttendanceData = sortedAttendanceData.filter((item) => {
    if (!searchTerm.trim()) return true;
    const staffName = item.staff?.name?.toLowerCase() || '';
    const searchLower = searchTerm.toLowerCase().trim();
    return staffName.startsWith(searchLower) || staffName.includes(searchLower);
  });
  
  const staffTz = (item) =>
    item.attendance?.display_timezone || item.display_timezone || item.staff?.timezone || 'UTC';

  const getWorkBreakLate = (item) => {
    const staffStats = getTodayStatsForStaff(item.staff.staff_id);
    const isActiveSession = item.attendance?.check_in && !item.attendance?.check_out;
    const historicalWorkSecs = (staffStats?.total_work_minutes || 0) * 60;
    const currentWorkSecs = isActiveSession ? getTotalWorkTime(item) : 0;
    const combinedWorkSecs = historicalWorkSecs + currentWorkSecs;
    const historicalBreakSecs = Math.round((staffStats?.total_break_minutes || 0) * 60);
    const ongoingBreakSecs = isActiveSession ? getOngoingBreakSeconds(item) : 0;
    const combinedBreakSecs = historicalBreakSecs + ongoingBreakSecs;
    return {
      staffStats,
      isActiveSession,
      workLabel: combinedWorkSecs > 0
        ? (isActiveSession ? formatDuration(combinedWorkSecs) : formatMinutesToTime(staffStats?.total_work_minutes))
        : null,
      workCount: staffStats?.sessions_count || 0,
      breakLabel: combinedBreakSecs > 0
        ? (isActiveSession ? formatDuration(combinedBreakSecs) : formatMinutesToTime(staffStats?.total_break_minutes))
        : null,
      breakCount: staffStats?.break_count || 0,
      lateBy: item.attendance?.late_by || 0,
      excessive: combinedBreakSecs > combinedWorkSecs && combinedWorkSecs > 0,
    };
  };

  const primaryKpis = [
    { title: 'Check-In', value: detailedStats.totalCheckIn, icon: LogIn, accent: 'from-indigo-500 to-purple-500', soft: 'bg-indigo-50 text-indigo-700' },
    { title: 'Late', value: detailedStats.totalLate, icon: Clock, accent: 'from-amber-500 to-orange-500', soft: 'bg-amber-50 text-amber-800' },
    { title: 'On Break', value: detailedStats.onBreak, icon: Coffee, accent: 'from-orange-500 to-rose-400', soft: 'bg-orange-50 text-orange-700' },
    { title: 'Ready', value: detailedStats.ready, icon: CheckCircle, accent: 'from-purple-500 to-indigo-500', soft: 'bg-purple-50 text-purple-700' },
  ];
  const secondaryKpis = [
    { title: 'Check-Out', value: detailedStats.totalCheckOut, icon: LogOut, soft: 'bg-sky-50 text-sky-700' },
    { title: 'Absent', value: detailedStats.totalAbsent, icon: UserX, soft: 'bg-rose-50 text-rose-700' },
    { title: 'Not Marked', value: detailedStats.notMarked, icon: AlertCircle, soft: 'bg-slate-100 text-slate-600' },
    { title: 'Force Break', value: detailedStats.forceBreak, icon: Zap, soft: 'bg-violet-50 text-violet-700' },
  ];

  const dateLabel = selectedDate
    ? new Date(`${selectedDate}T12:00:00`).toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      })
    : '…';

  const setAttendanceView = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('admin_attendance_view', mode);
    } catch {
      /* ignore */
    }
  };

  const getIdleLevels = (item) => {
    const status = item.attendance?.status;
    const staffStats = getTodayStatsForStaff(item.staff.staff_id);
    const idleLevels = [];
    if (staffStats && status !== 'absent') {
      if (staffStats.idle_level_1_minutes > 0)
        idleLevels.push({
          n: 1,
          mins: staffStats.idle_level_1_minutes,
          count: staffStats.idle_level_1_count || 0,
          cls: 'bg-amber-100 text-amber-800',
        });
      if (staffStats.idle_level_2_minutes > 0)
        idleLevels.push({
          n: 2,
          mins: staffStats.idle_level_2_minutes,
          count: staffStats.idle_level_2_count || 0,
          cls: 'bg-orange-100 text-orange-800',
        });
      if (staffStats.idle_level_3_minutes > 0)
        idleLevels.push({
          n: 3,
          mins: staffStats.idle_level_3_minutes,
          count: staffStats.idle_level_3_count || 0,
          cls: 'bg-rose-100 text-rose-800',
        });
    }
    return idleLevels;
  };

  const renderIdleCell = (item, idleLevels) => {
    const idleTotal = idleLevels.reduce((s, l) => s + l.mins, 0);
    if (!idleLevels.length) {
      return <span className="text-sm font-semibold text-slate-300">—</span>;
    }
    return (
      <TooltipProvider delayDuration={120}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              {idleLevels.map((l) => (
                <button
                  key={l.n}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/admin/attendance/detail/${item.staff.staff_id}?tab=idle&level=${l.n}&date=${selectedDate}`
                    );
                  }}
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-bold ${l.cls}`}
                  data-testid={`idle-l${l.n}-${item.staff.staff_id}`}
                >
                  {l.n}
                </button>
              ))}
              <span className="ml-0.5 text-xs font-semibold tabular-nums text-slate-600">
                {formatMinutesToTime(idleTotal)}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            {idleLevels.map((l) => (
              <p key={l.n}>
                L{l.n}: {formatMinutesToTime(l.mins)}
                {l.count ? ` · ${l.count}×` : ''}
              </p>
            ))}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderActions = (item, activity, { compact = false } = {}) => {
    const btn = compact ? 'h-8 rounded-xl text-xs px-2.5' : 'h-11 rounded-2xl';
    const grid = compact ? 'flex flex-wrap gap-1.5' : 'grid grid-cols-2 gap-2.5';
    const iconCls = compact ? 'w-3.5 h-3.5 mr-1' : 'w-4 h-4 mr-1.5';

    if (!item.attendance || item.attendance.status === 'not_marked') {
      return (
        <div className={grid}>
          <Button
            className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-600/25`}
            onClick={() => handleMarkAttendance(item.staff.staff_id, 'present')}
            data-testid={`mark-present-${item.staff.staff_id}`}
          >
            Present
          </Button>
          <Button
            variant="outline"
            className={`${btn} border-slate-200 text-slate-700 hover:bg-slate-50`}
            onClick={() => handleMarkAttendance(item.staff.staff_id, 'absent')}
            data-testid={`mark-absent-${item.staff.staff_id}`}
          >
            Absent
          </Button>
        </div>
      );
    }
    if (activity.status === 'ready') {
      return (
        <div className={grid}>
          <Button
            className={`${btn} bg-violet-600 hover:bg-violet-700 text-white shadow-md shadow-violet-600/25`}
            onClick={() => handleForceBreak(item.staff.staff_id, item.staff.name)}
            data-testid={`force-break-${item.staff.staff_id}`}
          >
            <Pause className={iconCls} />
            {compact ? 'Break' : 'Force Break'}
          </Button>
          <Button
            variant="outline"
            className={`${btn} border-rose-200 text-rose-700 hover:bg-rose-50`}
            onClick={() => handleForceLogout(item.staff.staff_id, item.staff.name)}
            data-testid={`force-logout-${item.staff.staff_id}`}
          >
            <LogOut className={iconCls} />
            Logout
          </Button>
        </div>
      );
    }
    if (activity.status === 'on_break') {
      return (
        <div className={grid}>
          <Button
            className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-600/25`}
            onClick={() => handleEndForceBreak(item.staff.staff_id, item.staff.name)}
            data-testid={`end-break-${item.staff.staff_id}`}
          >
            <CheckCircle className={iconCls} />
            End Break
          </Button>
          <Button
            variant="outline"
            className={`${btn} border-rose-200 text-rose-700 hover:bg-rose-50`}
            onClick={() => handleForceLogout(item.staff.staff_id, item.staff.name)}
            data-testid={`force-logout-${item.staff.staff_id}`}
          >
            <LogOut className={iconCls} />
            Logout
          </Button>
        </div>
      );
    }
    return (
      <div
        className={
          compact
            ? 'text-xs font-medium text-slate-400'
            : 'rounded-2xl bg-slate-50 py-3 text-center text-sm font-medium text-slate-400'
        }
      >
        Already marked
      </div>
    );
  };

  const statusLabel = (status) => {
    if (status === 'late') return 'Late';
    if (status === 'present') return 'Present';
    if (status === 'absent') return 'Absent';
    if (status === 'day_off') return 'Off Day';
    return 'Not Marked';
  };

  const statusClass = (status) => {
    if (status === 'late') return 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:ring-amber-800/50';
    if (status === 'present') return 'bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-300 dark:ring-indigo-800/50';
    if (status === 'absent') return 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/50 dark:text-rose-300 dark:ring-rose-800/50';
    if (status === 'day_off') return 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-800/50';
    return 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  return (
    <div className="relative pb-8" data-testid="attendance-page">
      {/* Soft page atmosphere */}
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
        {/* Hero toolbar */}
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 sm:px-6 sm:py-5 text-white">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t('attendance.title')}</h2>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold backdrop-blur ${
                    isConnected ? 'bg-white/20 text-white' : 'bg-amber-400/30 text-amber-50'
                  }`}>
                    <span className={`h-2 w-2 rounded-full bg-white ${isConnected ? 'att-live-dot' : 'animate-pulse'}`} />
                    {isConnected ? 'Live' : 'Polling 5s'}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-indigo-50/90">
                  Floor monitor · check-in times in each staff timezone
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center rounded-2xl bg-white/15 p-1 ring-1 ring-white/25 backdrop-blur">
                  <Button variant="ghost" size="icon"
                    onClick={() => {
                      const [y, m, d] = selectedDate.split('-').map(Number);
                      setSelectedDate(new Date(Date.UTC(y, m - 1, d - 1)).toISOString().split('T')[0]);
                    }}
                    className="h-9 w-9 rounded-xl text-white hover:bg-white/20"
                    data-testid="date-prev-button">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                    className="h-9 w-[9.75rem] rounded-xl border-0 bg-transparent px-1 text-sm font-semibold text-white focus:outline-none [color-scheme:dark]"
                    data-testid="date-picker" />
                  <Button variant="ghost" size="icon" disabled={isToday}
                    onClick={() => {
                      const [y, m, d] = selectedDate.split('-').map(Number);
                      setSelectedDate(new Date(Date.UTC(y, m - 1, d + 1)).toISOString().split('T')[0]);
                    }}
                    className="h-9 w-9 rounded-xl text-white hover:bg-white/20 disabled:opacity-40"
                    data-testid="date-next-button">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <Button variant="ghost" size="icon"
                  className="h-11 w-11 rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
                  onClick={() => { fetchAttendance(); fetchStats(); if (isToday) fetchTodayStats(); }}
                  data-testid="refresh-button">
                  <RefreshCcw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="px-4 py-3 sm:px-6 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70">
            <p className="text-sm font-medium text-slate-600">{dateLabel}</p>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 font-medium text-slate-700 ring-1 ring-slate-200">
                <Globe className="h-3 w-3 text-purple-600" /> Staff timezones
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                <Timer className="h-3 w-3" /> {currentTime.toLocaleTimeString()}
              </span>
              {lastUpdate && <span>Updated {lastUpdate.toLocaleTimeString()}</span>}
            </div>
          </div>
        </section>

        {/* Primary KPIs */}
        <section className="att-fade-up grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" style={{ animationDelay: '60ms' }}>
          {primaryKpis.map((k) => (
            <div key={k.title}
              data-testid={`stat-${k.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg">
              <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${k.accent} opacity-10 blur-xl transition group-hover:opacity-20`} />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k.title}</p>
                  <p className="mt-2 text-3xl sm:text-4xl font-bold tabular-nums tracking-tight text-slate-900">{k.value}</p>
                </div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${k.soft}`}>
                  <k.icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Secondary KPIs — swipe on mobile */}
        <section className="att-fade-up att-kpi-scroll md:!grid" style={{ animationDelay: '120ms' }}>
          {secondaryKpis.map((k) => (
            <div key={k.title}
              data-testid={`stat-${k.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="rounded-2xl border border-slate-200/70 bg-white/90 px-3.5 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${k.soft}`}>
                  <k.icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xl font-bold tabular-nums text-slate-900">{k.value}</p>
                  <p className="text-[11px] font-medium text-slate-500">{k.title}</p>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Staff roster — cards or table */}
        <section className="att-fade-up space-y-4" style={{ animationDelay: '180ms' }} data-testid="attendance-table">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-xl font-bold tracking-tight text-slate-900">Live roster</h3>
              <p className="text-sm text-slate-500">
                {dateLabel} · {filteredAttendanceData.length} staff
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <div
                className="inline-flex shrink-0 items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm"
                data-testid="attendance-view-toggle"
              >
                <button
                  type="button"
                  onClick={() => setAttendanceView('cards')}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-xs font-semibold transition ${
                    viewMode === 'cards'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  }`}
                  data-testid="view-cards-btn"
                  aria-pressed={viewMode === 'cards'}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  Cards
                </button>
                <button
                  type="button"
                  onClick={() => setAttendanceView('table')}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-xs font-semibold transition ${
                    viewMode === 'table'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  }`}
                  data-testid="view-table-btn"
                  aria-pressed={viewMode === 'table'}
                >
                  <Table2 className="h-3.5 w-3.5" />
                  Table
                </button>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search staff…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-11 rounded-2xl border-slate-200 bg-white/90 pl-10 shadow-sm backdrop-blur"
                  data-testid="staff-search-input"
                />
              </div>
            </div>
          </div>

          {filteredAttendanceData.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-white/80 px-6 py-20 text-center text-slate-500">
              {searchTerm ? `No staff matching "${searchTerm}"` : 'No staff found'}
            </div>
          ) : viewMode === 'table' ? (
            <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_18px_40px_-28px_rgba(15,23,42,0.45)]">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/90 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      <th className="px-4 py-3.5 sm:px-5">Staff</th>
                      <th className="px-3 py-3.5">Status</th>
                      <th className="px-3 py-3.5">Check in</th>
                      <th className="px-3 py-3.5">Live / Out</th>
                      <th className="px-3 py-3.5">Work</th>
                      <th className="px-3 py-3.5">Break</th>
                      <th className="px-3 py-3.5">Idle</th>
                      <th className="px-4 py-3.5 text-right sm:px-5">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredAttendanceData.map((item) => {
                      const activity = getActivityStatus(item);
                      const metrics = getWorkBreakLate(item);
                      const tz = staffTz(item);
                      const status = item.attendance?.status;
                      const isBreak = activity.status === 'on_break';
                      const isReady = activity.status === 'ready';
                      const lateBy = metrics.lateBy || 0;
                      const idleLevels = getIdleLevels(item);

                      return (
                        <tr
                          key={item.staff.staff_id}
                          data-testid={`attendance-row-${item.staff.staff_id}`}
                          className={`group cursor-pointer transition hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20 ${
                            isBreak
                              ? 'bg-orange-50/30'
                              : isReady
                                ? 'bg-indigo-50/20'
                                : ''
                          }`}
                          onClick={() =>
                            navigate(
                              `/admin/attendance/detail/${item.staff.staff_id}?tab=overview&date=${selectedDate}`
                            )
                          }
                        >
                          <td className="px-4 py-3.5 sm:px-5">
                            <div className="flex min-w-0 items-center gap-3">
                              <div
                                className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white ${
                                  isBreak
                                    ? 'bg-gradient-to-br from-orange-500 to-rose-500'
                                    : isReady
                                      ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                                      : 'bg-gradient-to-br from-slate-600 to-slate-800'
                                }`}
                              >
                                {(item.staff.name || '?').charAt(0).toUpperCase()}
                                {(isBreak || isReady) && (
                                  <span
                                    className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${
                                      isBreak ? 'bg-orange-400 att-live-dot' : 'bg-indigo-400 att-live-dot'
                                    }`}
                                  />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate font-semibold text-slate-900">{item.staff.name}</p>
                                <p className="truncate text-xs text-slate-500">
                                  {item.staff.department || 'No department'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3.5">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ${statusClass(status)}`}
                            >
                              {statusLabel(status)}
                              {lateBy > 0 ? ` · ${lateBy}m` : ''}
                            </span>
                          </td>
                          <td className="px-3 py-3.5">
                            {item.attendance?.check_in ? (
                              <div>
                                <p className="font-semibold tabular-nums text-slate-900">
                                  {formatTimeFromISO(item.attendance.check_in, tz)}
                                </p>
                                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
                                  <DeviceIcon
                                    deviceType={item.attendance.check_in_device}
                                    lastActive={null}
                                    isActive={false}
                                    className="w-3 h-3"
                                  />
                                  {tz}
                                </p>
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3.5">
                            {isBreak || isReady ? (
                              <div>
                                <p
                                  className={`font-bold tabular-nums att-timer-live ${
                                    isBreak ? 'text-orange-600' : 'text-indigo-600'
                                  }`}
                                >
                                  {formatDuration(activity.duration || 0)}
                                </p>
                                <p className="text-[11px] font-semibold text-slate-500">
                                  {isBreak
                                    ? activity.breakType === 'force'
                                      ? 'Force break'
                                      : 'On break'
                                    : 'Working'}
                                </p>
                              </div>
                            ) : item.attendance?.check_out ? (
                              <div>
                                <p className="font-semibold tabular-nums text-slate-900">
                                  {formatTimeFromISO(item.attendance.check_out, tz)}
                                </p>
                                <p className="text-[11px] text-slate-400">{tz}</p>
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3.5">
                            <span
                              className={`font-semibold tabular-nums ${
                                metrics.excessive ? 'text-rose-600' : 'text-slate-900'
                              }`}
                            >
                              {metrics.workLabel || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-3.5">
                            <span
                              className={`font-semibold tabular-nums ${
                                metrics.excessive ? 'text-rose-600' : 'text-slate-900'
                              }`}
                            >
                              {metrics.breakLabel || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-3.5">{renderIdleCell(item, idleLevels)}</td>
                          <td
                            className="px-4 py-3.5 text-right sm:px-5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {renderActions(item, activity, { compact: true })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredAttendanceData.map((item, idx) => {
                const activity = getActivityStatus(item);
                const metrics = getWorkBreakLate(item);
                const tz = staffTz(item);
                const status = item.attendance?.status;
                const isBreak = activity.status === 'on_break';
                const isReady = activity.status === 'ready';
                const lateBy = metrics.lateBy || 0;
                const idleLevels = getIdleLevels(item);

                return (
                  <article
                    key={item.staff.staff_id}
                    data-testid={`attendance-row-${item.staff.staff_id}`}
                    className={`att-fade-up group relative flex flex-col overflow-hidden rounded-[1.75rem] border bg-white shadow-[0_18px_40px_-28px_rgba(15,23,42,0.45)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_50px_-24px_rgba(15,23,42,0.35)] ${
                      isBreak
                        ? 'border-orange-200/80'
                        : isReady
                          ? 'border-indigo-200/80'
                          : 'border-slate-200/70'
                    }`}
                    style={{ animationDelay: `${Math.min(idx, 8) * 40 + 160}ms` }}
                  >
                    <div
                      className={`h-1.5 w-full ${
                        isBreak
                          ? 'bg-gradient-to-r from-orange-400 via-rose-400 to-violet-400'
                          : isReady
                            ? 'bg-gradient-to-r from-indigo-400 to-purple-500'
                            : lateBy
                              ? 'bg-gradient-to-r from-amber-400 to-orange-400'
                              : 'bg-gradient-to-r from-slate-200 to-slate-300'
                      }`}
                    />

                    <div className="flex flex-1 flex-col p-5">
                      <div
                        role="link"
                        tabIndex={0}
                        onClick={() =>
                          navigate(
                            `/admin/attendance/detail/${item.staff.staff_id}?tab=overview&date=${selectedDate}`
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(
                              `/admin/attendance/detail/${item.staff.staff_id}?tab=overview&date=${selectedDate}`
                            );
                          }
                        }}
                        className="flex flex-1 cursor-pointer flex-col rounded-2xl outline-none ring-indigo-400/0 transition hover:bg-slate-50/60 dark:hover:bg-slate-800/50 focus-visible:ring-2 focus-visible:ring-indigo-400/40"
                        data-testid={`open-detail-${item.staff.staff_id}`}
                      >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-white shadow-lg ${
                              isBreak
                                ? 'bg-gradient-to-br from-orange-500 to-rose-500 shadow-orange-500/25'
                                : isReady
                                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-500/25'
                                  : 'bg-gradient-to-br from-slate-600 to-slate-800 shadow-slate-700/20'
                            }`}
                          >
                            {(item.staff.name || '?').charAt(0).toUpperCase()}
                            {(isBreak || isReady) && (
                              <span
                                className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-[2.5px] border-white ${
                                  isBreak ? 'bg-orange-400 att-live-dot' : 'bg-indigo-400 att-live-dot'
                                }`}
                              />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h4 className="truncate text-lg font-bold tracking-tight text-slate-900">
                              {item.staff.name}
                            </h4>
                            <p className="truncate text-sm text-slate-500">
                              {item.staff.department || 'No department'}
                            </p>
                          </div>
                        </div>
                        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ${statusClass(status)}`}>
                          {statusLabel(status)}
                          {lateBy > 0 ? ` · ${lateBy}m` : ''}
                        </span>
                      </div>

                      <div className="mt-5 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-slate-50 px-3.5 py-3.5 ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-800">
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                            Check in
                          </p>
                          {item.attendance?.check_in ? (
                            <>
                              <p className="mt-1.5 text-xl font-bold tabular-nums tracking-tight text-slate-900">
                                {formatTimeFromISO(item.attendance.check_in, tz)}
                              </p>
                              <p className="mt-1 flex items-center gap-1 truncate text-[11px] text-slate-400">
                                <DeviceIcon
                                  deviceType={item.attendance.check_in_device}
                                  lastActive={null}
                                  isActive={false}
                                  className="w-3 h-3"
                                />
                                {tz}
                              </p>
                            </>
                          ) : (
                            <p className="mt-1.5 text-xl font-semibold text-slate-300">—</p>
                          )}
                        </div>

                        <div
                          className={`rounded-2xl px-3.5 py-3.5 ring-1 ${
                            isBreak
                              ? 'bg-orange-50 ring-orange-100 dark:bg-orange-950/50 dark:ring-orange-800/50'
                              : isReady
                                ? 'bg-indigo-50 ring-indigo-100 dark:bg-indigo-950/50 dark:ring-indigo-800/50'
                                : 'bg-slate-50 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-800'
                          }`}
                        >
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                            {isBreak || isReady ? 'Live now' : 'Check out'}
                          </p>
                          {isBreak || isReady ? (
                            <>
                              <p
                                className={`mt-1.5 text-xl font-bold tabular-nums tracking-tight att-timer-live ${
                                  isBreak ? 'text-orange-600 dark:text-orange-400' : 'text-indigo-600 dark:text-indigo-400'
                                }`}
                              >
                                {formatDuration(activity.duration || 0)}
                              </p>
                              <p className="mt-1 text-[11px] font-semibold text-slate-500">
                                {isBreak
                                  ? activity.breakType === 'force'
                                    ? 'Force break'
                                    : 'On break'
                                  : 'Working'}
                              </p>
                            </>
                          ) : item.attendance?.check_out ? (
                            <>
                              <p className="mt-1.5 text-xl font-bold tabular-nums tracking-tight text-slate-900">
                                {formatTimeFromISO(item.attendance.check_out, tz)}
                              </p>
                              <p className="mt-1 truncate text-[11px] text-slate-400">{tz}</p>
                            </>
                          ) : (
                            <p className="mt-1.5 text-xl font-semibold text-slate-300">—</p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-3 overflow-hidden rounded-2xl ring-1 ring-slate-100">
                        <div className="bg-white px-3 py-3 text-left">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Work</p>
                          <p
                            className={`mt-1 text-sm font-bold tabular-nums ${
                              metrics.excessive ? 'text-rose-600' : 'text-slate-900'
                            }`}
                          >
                            {metrics.workLabel || '—'}
                          </p>
                        </div>
                        <div className="border-x border-slate-100 bg-white px-3 py-3 text-left">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Break</p>
                          <p
                            className={`mt-1 text-sm font-bold tabular-nums ${
                              metrics.excessive ? 'text-rose-600' : 'text-slate-900'
                            }`}
                          >
                            {metrics.breakLabel || '—'}
                          </p>
                        </div>
                        <div
                          className="bg-white px-3 py-3"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Idle</p>
                          <div className="mt-1">{renderIdleCell(item, idleLevels)}</div>
                        </div>
                      </div>
                      </div>

                      <div
                        className="mt-5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderActions(item, activity)}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <div className="flex justify-center">
          <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium shadow-sm ring-1 ${
            isConnected
              ? 'bg-indigo-50 text-indigo-700 ring-indigo-100'
              : 'bg-amber-50 text-amber-700 ring-amber-100'
          }`}>
            <span className={`h-2 w-2 rounded-full ${isConnected ? 'bg-indigo-500 att-live-dot' : 'bg-amber-500 animate-pulse'}`} />
            {isConnected ? 'Real-time updates active' : 'Auto-refresh every 5 seconds'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AttendancePage;
